import { useEffect, useMemo, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, XCircle, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function Purchases() {
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    supplier_id: "", invoice_no: "", invoice_date: today, items: [], discount: 0, notes: "",
  });

  const load = async () => {
    const [p, s, i] = await Promise.all([
      api.get("/purchases"),
      api.get("/suppliers"),
      api.get("/inventory"),
    ]);
    setList(p.data); setSuppliers(s.data); setInventory(i.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({
      supplier_id: suppliers[0]?.id || "", invoice_no: "", invoice_date: today,
      items: [], discount: 0, notes: "",
    });
    setOpen(true);
  };

  const addItem = (inv) => {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        inventory_id: inv?.id || null,
        name: inv?.name || "",
        unit: inv?.unit || "kg",
        qty: 1,
        rate: inv?.cost || 0,
        gst_rate: 5,
      }],
    }));
  };

  const updateItem = (idx, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => {
    let sub = 0, cgst = 0, sgst = 0;
    for (const it of form.items) {
      const line = Number(it.qty || 0) * Number(it.rate || 0);
      sub += line;
      const g = (line * Number(it.gst_rate || 0)) / 100;
      cgst += g / 2; sgst += g / 2;
    }
    const dAmt = Math.min(Number(form.discount || 0), sub);
    const ratio = sub > 0 ? (sub - dAmt) / sub : 0;
    cgst = +(cgst * ratio).toFixed(2);
    sgst = +(sgst * ratio).toFixed(2);
    const total = +(sub - dAmt + cgst + sgst).toFixed(2);
    return { sub: +sub.toFixed(2), dAmt: +dAmt.toFixed(2), cgst, sgst, total };
  }, [form.items, form.discount]);

  const save = async () => {
    if (!form.supplier_id) return toast.error("Choose a supplier");
    if (!form.invoice_no.trim()) return toast.error("Enter invoice number");
    if (form.items.length === 0) return toast.error("Add at least one item");
    try {
      await api.post("/purchases", { ...form, discount: Number(form.discount || 0) });
      toast.success("Purchase recorded · stock updated · ledger posted");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async (p) => {
    if (!window.confirm(`Cancel ${p.purchase_no}? This will reverse the inventory.`)) return;
    try { await api.post(`/purchases/${p.id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="purchases-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Purchases</h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Record vendor invoices · stock and supplier ledger update automatically.
          </p>
        </div>
        <Button onClick={startNew} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-purchase-btn">
          <Plus size={16} className="mr-1.5" /> New Purchase
        </Button>
      </div>

      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">GRN No</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Supplier</th>
              <th className="text-left px-4 py-2.5">Invoice #</th>
              <th className="text-right px-4 py-2.5">Items</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t border-[#EBE8E2]" data-testid={`purchase-row-${p.id}`}>
                <td className="px-4 py-2.5 font-mono-num font-semibold">{p.purchase_no}</td>
                <td className="px-4 py-2.5 text-[#7A7873] font-mono-num text-xs">{p.invoice_date}</td>
                <td className="px-4 py-2.5">{p.supplier_name}</td>
                <td className="px-4 py-2.5 font-mono-num text-xs">{p.invoice_no}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{(p.items || []).length}</td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(p.total)}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${p.status === "cancelled" ? "bg-[#F4F2EE] text-[#7A7873]" : "bg-[#E5EBE3] text-[#6A7F66]"}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setView(p)}>
                    <Receipt size={13} />
                  </Button>
                  {p.status !== "cancelled" && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => cancel(p)}>
                      <XCircle size={13} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#7A7873]">No purchases recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* New Purchase Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle>New Purchase Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Supplier *</Label>
                <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="purchase-supplier"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Invoice No *</Label>
                <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="purchase-invno" />
              </div>
              <div>
                <Label>Invoice Date</Label>
                <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
              </div>
            </div>

            <div className="border border-[#EBE8E2] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-right px-3 py-2 w-20">Qty</th>
                    <th className="text-left px-3 py-2 w-16">Unit</th>
                    <th className="text-right px-3 py-2 w-24">Rate</th>
                    <th className="text-right px-3 py-2 w-16">GST%</th>
                    <th className="text-right px-3 py-2 w-24">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => (
                    <tr key={i} className="border-t border-[#EBE8E2]">
                      <td className="px-2 py-1.5">
                        <Input value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} className="h-8 border-[#EBE8E2]" />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} className="h-8 border-[#EBE8E2] text-right font-mono-num" />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input value={it.unit} onChange={(e) => updateItem(i, { unit: e.target.value })} className="h-8 border-[#EBE8E2]" />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" value={it.rate} onChange={(e) => updateItem(i, { rate: e.target.value })} className="h-8 border-[#EBE8E2] text-right font-mono-num" />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" value={it.gst_rate} onChange={(e) => updateItem(i, { gst_rate: e.target.value })} className="h-8 border-[#EBE8E2] text-right font-mono-num" />
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono-num">{inr(Number(it.qty || 0) * Number(it.rate || 0))}</td>
                      <td className="px-1 py-1.5">
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => removeItem(i)}>
                          <Trash2 size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {form.items.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-[#7A7873] text-xs">No items added.</td></tr>
                  )}
                </tbody>
              </table>
              <div className="border-t border-[#EBE8E2] bg-[#FBFAF8] p-2 flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => addItem(null)} data-testid="add-purchase-item">
                  <Plus size={12} className="mr-1" /> Custom item
                </Button>
                <Select onValueChange={(v) => addItem(inventory.find((x) => x.id === v))}>
                  <SelectTrigger className="h-8 w-48 border-[#EBE8E2]"><SelectValue placeholder="+ From inventory" /></SelectTrigger>
                  <SelectContent>
                    {inventory.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label>Discount (₹)</Label>
                <Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
                <Label className="mt-2 block">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="resize-none h-16" />
              </div>
              <div className="bg-[#FBFAF8] border border-[#EBE8E2] rounded-md p-3 text-sm font-mono-num space-y-1">
                <div className="flex justify-between text-[#7A7873]"><span>Subtotal</span><span>{inr(totals.sub)}</span></div>
                {totals.dAmt > 0 && <div className="flex justify-between text-[#6A7F66]"><span>Discount</span><span>− {inr(totals.dAmt)}</span></div>}
                <div className="flex justify-between text-[#7A7873]"><span>CGST</span><span>{inr(totals.cgst)}</span></div>
                <div className="flex justify-between text-[#7A7873]"><span>SGST</span><span>{inr(totals.sgst)}</span></div>
                <div className="flex justify-between font-bold text-[#2D2C2A] text-base border-t border-[#EBE8E2] pt-1.5 mt-1.5">
                  <span>Total</span><span data-testid="purchase-total">{inr(totals.total)}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={save} data-testid="save-purchase">Record Purchase</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Purchase */}
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="bg-white max-w-md">
          {view && (
            <>
              <DialogHeader><DialogTitle>{view.purchase_no} · {view.supplier_name}</DialogTitle></DialogHeader>
              <div className="text-xs space-y-1 text-[#7A7873]">
                <div className="flex justify-between"><span>Invoice #</span><span className="font-mono-num">{view.invoice_no}</span></div>
                <div className="flex justify-between"><span>Date</span><span className="font-mono-num">{view.invoice_date}</span></div>
                <div className="flex justify-between"><span>Status</span><span className="capitalize">{view.status}</span></div>
              </div>
              <table className="w-full text-xs mt-3">
                <thead className="border-y border-[#EBE8E2]"><tr>
                  <th className="text-left py-1.5">Item</th>
                  <th className="text-right py-1.5 w-12">Qty</th>
                  <th className="text-right py-1.5 w-16">Rate</th>
                  <th className="text-right py-1.5 w-20">Amount</th>
                </tr></thead>
                <tbody>
                  {(view.items || []).map((it, i) => (
                    <tr key={i} className="border-b border-dashed border-[#EBE8E2]">
                      <td className="py-1.5">{it.name}</td>
                      <td className="py-1.5 text-right font-mono-num">{it.qty} {it.unit}</td>
                      <td className="py-1.5 text-right font-mono-num">{inr(it.rate)}</td>
                      <td className="py-1.5 text-right font-mono-num">{inr(it.qty * it.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-xs space-y-0.5 font-mono-num mt-2">
                <div className="flex justify-between"><span>Subtotal</span><span>{inr(view.subtotal)}</span></div>
                {view.discount > 0 && <div className="flex justify-between"><span>Discount</span><span>− {inr(view.discount)}</span></div>}
                <div className="flex justify-between"><span>CGST</span><span>{inr(view.cgst)}</span></div>
                <div className="flex justify-between"><span>SGST</span><span>{inr(view.sgst)}</span></div>
                <div className="flex justify-between font-bold text-sm pt-1 mt-1 border-t border-[#2D2C2A]">
                  <span>TOTAL</span><span>{inr(view.total)}</span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
